/**
 * Real HTTP client for orbit-platform-admin-service.
 *
 * Auth tokens (access + refresh) live in HttpOnly cookies set by the server.
 * localStorage holds only non-secret session state: operator profile, token
 * expiry timestamps, and impersonation context. Cookies are sent automatically
 * by the browser on every same-origin request; no Authorization header needed.
 * On 401 we attempt a cookie-based refresh; if that fails, we sign out.
 */

import type {
  AuditEvent,
  CostBreakdown,
  DebitOrder,
  DiscoveryRecord,
  MigrationStatus,
  ImpersonationSession,
  IntegrationKey,
  Operator,
  PlanLimits,
  PlanTier,
  PlatformOverview,
  Tenant,
  TenantUser,
  TenantUserRole,
  TicketAttachment,
  TicketDetail,
  TicketMessage,
  TicketPriority,
  TicketStatus,
  TicketSummary,
  UsageEvent,
  UsageRange
} from "../types";
import type {
  AcceptanceCriterion,
  OpsBoardResponse,
  OpsCard,
  OpsColumn,
  OpsEvent
} from "../types/board";
import type {
  ControlPanelResponse,
  OpsAgent,
  OpsAgentRun,
  OpsChatMessage,
  OpsFocus
} from "../types/agents";
import type {
  QuotaSnapshot, QuotaConfig, QuotaEvent, SurplusItem, BurndownPoint
} from "../types/quota";
import type {
  ReferrerPartner,
  ReferralSummary,
  ReferralDetail,
  ReferrerLeaderboardEntry,
  ReferralStatus,
  ReferrerStatus,
  ReferralUpdate
} from "../types/referrals";
import { costCents } from "./costRates";

// v3: tokens removed from storage (now HttpOnly cookies). Bump clears stale sessions.
const STORAGE_KEY = "orbit-platform-admin.session.v3";

// Same-origin by default. The SPA talks to the platform-admin-service through
// the `/platform-api` path, which is reverse-proxied to the service by:
//   - Caddy in cloud   (admin.<domain>/platform-api/* -> platform-admin-service:4181)
//   - the SPA's own nginx in local docker-compose (see Dockerfile / nginx.conf)
//   - the vite dev server proxy for `npm run dev` (see vite.config.ts)
// This means no absolute API hostname is ever baked into the build and there is
// no cross-origin/CORS surface. `||` (not `??`) so an empty build arg also
// falls back instead of producing a same-origin "" that hits the static server.
const API_BASE: string = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    ?.VITE_PLATFORM_ADMIN_API || "/platform-api"
).replace(/\/$/, "");

interface PersistedSession {
  operator: Operator;
  expiresAt: number;          // access-token expiry, epoch ms — drives proactive refresh
  refreshExpiresAt: number;   // hard session deadline (refresh-token expiry), epoch ms
  impersonation: ImpersonationSession | null;
  // Tokens intentionally absent: they live in HttpOnly cookies, not localStorage.
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

export function loadSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedSession) : null;
  } catch {
    return null;
  }
}

function persistSession(s: PersistedSession | null): void {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

function updateSession(patch: Partial<PersistedSession>): void {
  const current = loadSession();
  if (!current) return;
  persistSession({ ...current, ...patch });
}

// ---------------------------------------------------------------------------
// HTTP plumbing — auto-refresh on 401, surfaced errors for the UI.
// ---------------------------------------------------------------------------

class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Clean, operator-facing message for a status with no structured API body. */
function messageForStatus(status: number): string {
  switch (status) {
    case 400: return "That request wasn't valid. Please check your input and try again.";
    case 401: return "Incorrect email or password.";
    case 403: return "You don't have access to the platform admin console.";
    case 404: return "That resource couldn't be found.";
    case 405:
    case 502:
    case 503:
    case 504:
      return "The admin service is unavailable right now. Please try again shortly.";
    default:
      return status >= 500
        ? "Something went wrong on our side. Please try again shortly."
        : `Request failed (${status}).`;
  }
}

async function rawFetch<T>(path: string, init: RequestInit = {}, withAuth = true): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...((init.headers as Record<string, string>) ?? {})
  };
  // No Authorization header — the access token travels as an HttpOnly cookie.
  // credentials: 'include' ensures cookies are sent even when Vite dev server
  // proxies to a different port (cross-origin during local `npm run dev`).
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: "include" });
  } catch {
    // Network-level failure (DNS, connection refused, CORS, offline). The
    // service URL is wrong or the API is down — never expose the raw cause.
    throw new ApiError(0, "Can't reach the admin service. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as unknown as T;

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const text = await res.text();
  let body: unknown = null;
  if (text && isJson) {
    try { body = JSON.parse(text); } catch { body = null; }
  } else if (text) {
    // Try anyway — some misconfigured proxies omit the content-type header — but
    // only KEEP the parsed value if it's actually JSON. Raw HTML/plain-text
    // bodies (e.g. an nginx "405 Not Allowed" page) are discarded so they can
    // never be rendered to the operator.
    try { body = JSON.parse(text); } catch { body = null; }
  }

  if (!res.ok) {
    const err = (body && typeof body === "object" ? body : null) as
      | { error?: string; message?: string; code?: string }
      | null;
    const serverMessage = err?.error ?? err?.message;
    // Prefer a structured message from our API; otherwise map the status to a
    // clean, human sentence. We deliberately never fall back to `text`.
    const message = serverMessage ?? messageForStatus(res.status);
    throw new ApiError(res.status, message, err?.code);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Session death — the whole point of this module's auth handling. When a
// session can no longer be revived we DON'T just silently clear localStorage
// and leave the operator staring at a dead, frozen UI (every click failing).
// We clear it AND hard-redirect to /login so the only possible next state is a
// fresh sign-in. A full-page navigation also guarantees in-memory React state
// is wiped — a clean logout, every time.
// ---------------------------------------------------------------------------

/** True once a logout has been triggered, so concurrent failures don't each
 *  fire their own redirect (which can cancel/loop the navigation). */
let loggingOut = false;

/** Clear the session and bounce to the login screen. Idempotent. */
export function forceLogout(reason: "expired" | "manual" = "expired"): void {
  persistSession(null);
  if (typeof window === "undefined") return;
  if (loggingOut) return;
  loggingOut = true;
  const onLogin = window.location.pathname === "/login";
  if (onLogin) { loggingOut = false; return; }
  const suffix = reason === "expired" ? "?expired=1" : "";
  // Hard navigation (not react-router) so nothing in memory survives.
  window.location.assign(`/login${suffix}`);
}

/** Is the session past its hard deadline (refresh token expired)? */
export function isSessionExpired(session: PersistedSession | null = loadSession()): boolean {
  if (!session) return true;
  return Date.now() >= session.refreshExpiresAt;
}

// Single-flight refresh. The refresh token is single-use and rotates on the
// server, so two concurrent refreshes would revoke each other and bounce a
// perfectly valid session. We coalesce all callers onto one in-flight refresh.
let inflightRefresh: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    const session = loadSession();
    if (!session) throw new ApiError(401, "Session expired.");
    // No body — the refresh token is in the orbit_rt HttpOnly cookie.
    const next = await rawFetch<{
      operator: Operator;
      expiresIn: number;
      refreshExpiresIn?: number;
    }>("/v1/auth/refresh", { method: "POST" }, false);
    const current = loadSession() ?? session;
    persistSession({
      ...current,
      operator: next.operator,
      expiresAt: Date.now() + next.expiresIn * 1000,
      refreshExpiresAt:
        typeof next.refreshExpiresIn === "number"
          ? Date.now() + next.refreshExpiresIn * 1000
          : current.refreshExpiresAt
    });
  })().finally(() => { inflightRefresh = null; });
  return inflightRefresh;
}

// A reverse-proxy / gateway blip — Caddy swapping a container mid-deploy, a
// dropped keep-alive socket, a cold upstream — surfaces as a one-off 502/503/504
// (or a 405 / network-level status 0) on the FIRST request, then works on an
// immediate retry. Without this, the operator sees a spurious "admin service is
// unavailable" and has to click "Try again" by hand (e.g. opening a support
// ticket). Absorb these transparently.
//
// ONLY for idempotent GETs — a POST/PATCH/DELETE must never be silently replayed,
// as it may have already partially applied on the server.
const TRANSIENT_STATUSES = new Set([0, 405, 502, 503, 504]);

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function rawFetchRetrying<T>(path: string, init: RequestInit, withAuth: boolean): Promise<T> {
  const idempotent = (init.method ?? "GET").toUpperCase() === "GET";
  const maxAttempts = idempotent ? 3 : 1;
  for (let attempt = 1; ; attempt++) {
    try {
      return await rawFetch<T>(path, init, withAuth);
    } catch (err) {
      const transient = err instanceof ApiError && TRANSIENT_STATUSES.has(err.status);
      if (!transient || attempt >= maxAttempts) throw err;
      await sleep(attempt * 250); // brief backoff: 250ms, then 500ms
    }
  }
}

/** Try a refresh, retry the original request. If the session is truly dead,
 *  force a clean logout (redirect to /login) instead of leaving a frozen UI.
 *  Transient gateway blips are absorbed by rawFetchRetrying (idempotent GETs only). */
async function withRefresh<T>(path: string, init: RequestInit, withAuth: boolean): Promise<T> {
  try {
    return await rawFetchRetrying<T>(path, init, withAuth);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401 || !withAuth) throw err;
    try {
      await refreshSession();
    } catch {
      forceLogout("expired");
      throw err;
    }
    try {
      return await rawFetchRetrying<T>(path, init, withAuth);
    } catch (err2) {
      // Still unauthorised even with a freshly-minted token → genuinely dead.
      if (err2 instanceof ApiError && err2.status === 401) forceLogout("expired");
      throw err2;
    }
  }
}

/**
 * Proactive keep-alive / reaper, driven by AppLayout on focus + a light timer.
 * - Hard deadline passed  → force a clean logout now (no dead UI to click into).
 * - Access token expiring  → refresh ahead of the next request so active work
 *                            never trips a 401; if that refresh fails, log out.
 * Returns nothing; it mutates session state and may redirect.
 */
export async function maintainSession(): Promise<void> {
  const session = loadSession();
  if (!session) return; // route guard owns the no-session case
  if (isSessionExpired(session)) { forceLogout("expired"); return; }
  // Refresh when within 60s of access-token expiry (or already past it).
  if (Date.now() < session.expiresAt - 60_000) return;
  try {
    await refreshSession();
  } catch {
    forceLogout("expired");
  }
}

function get<T>(path: string): Promise<T> {
  return withRefresh<T>(path, { method: "GET" }, true);
}
function post<T>(path: string, body?: unknown, withAuth = true): Promise<T> {
  return withRefresh<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }, withAuth);
}
function patch<T>(path: string, body: unknown): Promise<T> {
  return withRefresh<T>(path, { method: "PATCH", body: JSON.stringify(body) }, true);
}
function put<T>(path: string, body: unknown): Promise<T> {
  return withRefresh<T>(path, { method: "PUT", body: JSON.stringify(body) }, true);
}
function del<T>(path: string): Promise<T> {
  return withRefresh<T>(path, { method: "DELETE" }, true);
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function login(
  email: string,
  password: string
): Promise<{ session: PersistedSession | null; mfaRequired: boolean }> {
  // Tokens arrive as HttpOnly cookies (orbit_at, orbit_rt) — not in the body.
  const res = await rawFetch<{
    operator: Operator;
    mfaRequired?: boolean;
    expiresIn: number;
    refreshExpiresIn: number;
  }>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email: email.trim(), password })
  }, false);
  loggingOut = false;
  const session: PersistedSession = {
    operator: res.operator,
    expiresAt: Date.now() + res.expiresIn * 1000,
    refreshExpiresAt: Date.now() + res.refreshExpiresIn * 1000,
    impersonation: null
  };
  // Deliberately DO NOT persist while a code is outstanding. The cookies exist
  // but carry mfaVerified=false, so persisting here would let the SPA route to
  // a dashboard where every request 401s.
  if (res.mfaRequired) return { session: null, mfaRequired: true };
  persistSession(session);
  return { session, mfaRequired: false };
}

/** Submit the emailed sign-in code. On success the device is trusted for ~30 days. */
export async function verifyMfaCode(code: string): Promise<PersistedSession> {
  const res = await rawFetch<{
    operator: Operator;
    trustedForDays: number;
    expiresIn: number;
    refreshExpiresIn: number;
  }>("/v1/auth/mfa/verify", {
    method: "POST",
    body: JSON.stringify({ code: code.trim() })
  }, false);
  loggingOut = false;
  const session: PersistedSession = {
    operator: res.operator,
    expiresAt: Date.now() + res.expiresIn * 1000,
    refreshExpiresAt: Date.now() + res.refreshExpiresIn * 1000,
    impersonation: null
  };
  persistSession(session);
  return session;
}

/** Ask for a fresh code. Rate-limited server-side. */
export async function resendMfaCode(): Promise<void> {
  await rawFetch<{ sent: boolean }>("/v1/auth/mfa/resend", { method: "POST" }, false);
}

export async function logout(): Promise<void> {
  try {
    await post<void>("/v1/auth/logout");
  } catch {
    // If the server call fails (expired cookie, network), still clear local state
    // and let the server's own cookie clear (sent on best-effort).
  }
  persistSession(null);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  // Server re-issues fresh cookies (orbit_at, orbit_rt) after revoking all old sessions.
  const res = await post<{ expiresIn: number; refreshExpiresIn: number }>(
    "/v1/auth/change-password", { currentPassword, newPassword }
  );
  const current = loadSession();
  if (current) {
    persistSession({
      ...current,
      expiresAt: Date.now() + res.expiresIn * 1000,
      refreshExpiresAt: Date.now() + res.refreshExpiresIn * 1000
    });
  }
}

// ---------------------------------------------------------------------------
// Overview / dashboard
// ---------------------------------------------------------------------------

export function fetchOverview(): Promise<PlatformOverview> {
  return get<PlatformOverview>("/v1/overview");
}

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export function fetchTenants(): Promise<Tenant[]> {
  return get<Tenant[]>("/v1/tenants");
}

export interface AiUsageRow {
  tenantId: string;
  costUsd: number;
  turns: number;
  inputTokens: number;
  outputTokens: number;
}

/** Every tenant's current-month Assist spend (USD), for the Companies overview. */
export function fetchAiUsageAll(): Promise<{ month: string; tenants: AiUsageRow[] }> {
  return get<{ month: string; tenants: AiUsageRow[] }>("/v1/ai-usage");
}

export async function fetchTenant(id: string): Promise<Tenant | null> {
  try {
    return await get<Tenant>(`/v1/tenants/${encodeURIComponent(id)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** A vertical product package an operator can put a company on. */
export interface PackOption {
  pack: string;
  label: string;
  description: string;
  group: "comms" | "field" | "unified" | "commerce" | "enterprise" | "client";
  /** Seat (rep) cap; null = unlimited / custom (enterprise). */
  seats: number | null;
  /** Per-rep monthly list price in ZAR cents; null = custom-quoted. */
  perRepMonthlyCents: number | null;
  /** Whole-business monthly price in ZAR cents (flat-priced packs only, e.g. Orbit OS). */
  flatMonthlyCents?: number | null;
  /** Still sold? false = a legacy tier kept alive for existing tenants only. */
  selfServe?: boolean;
  /** What this package switches on for the customer (from config-service). */
  flags?: Record<string, string>;
}

/** Catalog of packages to choose from when creating / changing a company.
 *  Cached for the session — the catalog is static within a deploy. */
let _packsCache: Promise<{ packs: PackOption[] }> | null = null;
export function fetchPackages(): Promise<{ packs: PackOption[] }> {
  if (!_packsCache) {
    _packsCache = get<{ packs: PackOption[] }>("/v1/packages").catch((e) => {
      _packsCache = null; // allow retry on failure
      throw e;
    });
  }
  return _packsCache;
}

/** Change a tenant's vertical package (applies features + derives limits tier). */
export function changeTenantPack(id: string, pack: string): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(id)}/pack`, { pack });
}

export function createTenant(input: {
  name: string; slug?: string; industry: string; country: string;
  primaryContactEmail: string; primaryContactPhone: string;
  /** Vertical feature pack slug (e.g. "comms-pro"). Drives the tenant's features. */
  pack: string;
  /** Optional referrer partner to attribute this company to at creation. */
  referrerId?: string;
  notes?: string;
  /** Optional owner display name; defaults to the email handle when omitted. */
  ownerFullName?: string;
  /** Create the owner user + email a set-password link on create (default true). */
  sendOwnerInvite?: boolean;
  /** Operator-managed billing: start the tenant active so the owner skips the
   *  self-service payment wizard + email OTP (default true). */
  skipPayment?: boolean;
  /** Agreed billing terms. Omit to defer (company goes live, billing later). */
  billing?: BillingTerms;
}): Promise<CreatedTenant> {
  return post<CreatedTenant>("/v1/tenants", input);
}

export type CreatedTenant = Tenant & {
  ownerInvited?: boolean;
  ownerInviteEmailSent?: boolean;
  twilioSubaccountSid?: string | null;
  billingSummary?: string | null;
};

/**
 * Billing terms for an operator-managed company. `mode: "defer"` leaves the
 * company fully live with no schedule attached; `"schedule"` sets the free
 * period and first debit date the same way a paid signup does.
 *
 * The first debit date is always computed from the anchor + free period, never
 * typed, so the ledger and the bank can't drift apart.
 */
export interface BillingTerms {
  mode: "defer" | "schedule";
  monthlyAmountCents?: number;
  reps?: number;
  billingAnchor?: "day_25" | "last_day";
  /** Whole free months (0, 1 or 2). */
  freeMonths?: number;
  /** Explicit end of the free period for a bespoke deal; overrides freeMonths. */
  freeUntil?: string;
  mandateRef?: string;
  mandateStatus?: "pending" | "authorised" | "failed" | "cancelled";
  paymentProvider?: string;
}

/**
 * Attach or re-negotiate billing on an existing company — the other half of
 * creating with `mode: "defer"`. Rejects with 409 MANDATE_LOCKED if a live
 * mandate means the debit dates are already fixed at the bank; amount and seat
 * changes are always allowed.
 */
export function setTenantBilling(
  id: string,
  terms: BillingTerms
): Promise<Tenant & { billingSummary?: string }> {
  return post<Tenant & { billingSummary?: string }>(`/v1/tenants/${id}/billing`, terms);
}

export function updateTenant(id: string, patchInput: Partial<Tenant>): Promise<Tenant> {
  // Only forward fields the API accepts on PATCH /v1/tenants/:id (no usage, no billing,
  // no plan — those have dedicated endpoints).
  const safe: Record<string, unknown> = {};
  const allowed: (keyof Tenant)[] = [
    "name","slug","country","industry","primaryContactEmail","primaryContactPhone",
    "twilioSubaccountSid","status","notes","tags"
  ];
  for (const key of allowed) {
    if (patchInput[key] !== undefined) safe[key] = patchInput[key];
  }
  return patch<Tenant>(`/v1/tenants/${encodeURIComponent(id)}`, safe);
}

export function changeTenantPlan(id: string, plan: PlanTier): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(id)}/plan`, { plan });
}

export function overrideTenantLimits(id: string, customLimits: Partial<PlanLimits>): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(id)}/limits`, { customLimits });
}

export function suspendTenant(id: string, reason: string): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(id)}/suspend`, { reason });
}

export function unsuspendTenant(id: string): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(id)}/unsuspend`);
}

export async function deleteTenant(id: string): Promise<void> {
  await del<void>(`/v1/tenants/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Tenant users
// ---------------------------------------------------------------------------

export function fetchUsers(tenantId: string): Promise<TenantUser[]> {
  return get<TenantUser[]>(`/v1/tenants/${encodeURIComponent(tenantId)}/users`);
}

/**
 * Send an existing user a fresh set-password link.
 *
 * Separate from inviteUser because that one CREATES the login and 409s on
 * somebody who already has one — which is precisely the person who needs a
 * resend. The address comes from the record on file, not the caller.
 */
export function resendUserInvite(tenantId: string, userId: string): Promise<{ sent: boolean; email: string }> {
  return post<{ sent: boolean; email: string }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/resend-invite`,
    {}
  );
}

export function inviteUser(tenantId: string, input: {
  fullName: string; email: string; role: TenantUserRole; message?: string;
}): Promise<TenantUser & { inviteEmailSent?: boolean }> {
  return post<TenantUser & { inviteEmailSent?: boolean }>(`/v1/tenants/${encodeURIComponent(tenantId)}/users/invite`, input);
}

// ---------------------------------------------------------------------------
// Sales reps (bulk onboarding)
//
// A rep is TWO records that have to be linked: the `sales_agents` profile and
// the auth login. Inviting a user with role sales_agent through the single
// invite form used to create only the login, which the Android app reads as an
// owner. These endpoints do the whole job, and take a pasted list so a client's
// whole field team can be onboarded in one go before a training session.
// ---------------------------------------------------------------------------

export interface RepProfile {
  id: string;
  email: string | null;
  fullName: string;
  /** null = no login linked yet, so the phone app would treat them as an owner. */
  authUserId: string | null;
  status: string;
}

export interface RepInviteResult {
  email: string;
  fullName: string;
  agentId: string | null;
  profile: "created" | "existing" | "failed";
  /** "unknown" = the send timed out on our side; the email may still arrive. */
  invite: "sent" | "unknown" | "not_sent" | "skipped";
  linked: boolean;
  error?: string;
}

export interface BulkInviteSummary {
  tenantId: string;
  total: number;
  created: number;
  reinvited: number;
  sent: number;
  unknown: number;
  failed: number;
  results: RepInviteResult[];
}

export function fetchTenantReps(
  tenantId: string
): Promise<{ tenantId: string; total: number; withoutLogin: number; reps: RepProfile[] }> {
  return get(`/v1/tenants/${encodeURIComponent(tenantId)}/reps`);
}

/**
 * Bulk-invite reps. `raw` is whatever the operator pasted (newlines, commas,
 * semicolons, "Name <email>" all fine). Each row costs an SMTP send of ~7s and
 * they run 3 at a time, so budget ~40s for 15 reps — the caller must not use a
 * short client-side timeout or show a spinner it gives up on.
 */
export function bulkInviteReps(
  tenantId: string,
  input: { raw?: string; team?: string; jobTitle?: string; message?: string }
): Promise<BulkInviteSummary> {
  return post<BulkInviteSummary>(`/v1/tenants/${encodeURIComponent(tenantId)}/reps/bulk-invite`, input);
}

export function updateUserRole(userId: string, role: TenantUserRole): Promise<TenantUser> {
  // We don't know the tenantId from the userId in the UI's signature today.
  // The page that calls this already knows the tenant, so we encode a route
  // that lets the backend cross-check ownership.
  const tenantId = currentTenantHint();
  if (!tenantId) throw new Error("Tenant context required");
  return patch<TenantUser>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    { role }
  );
}

export async function removeUser(userId: string): Promise<void> {
  const tenantId = currentTenantHint();
  if (!tenantId) throw new Error("Tenant context required");
  await del<void>(`/v1/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`);
}

export type PasswordResetResult = { ok: boolean; sent: boolean; timedOut?: boolean };

export async function sendPasswordReset(userId: string): Promise<PasswordResetResult> {
  const tenantId = currentTenantHint();
  if (!tenantId) throw new Error("Tenant context required");
  return post<PasswordResetResult>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/password-reset`
  );
}

/**
 * Lightweight tenant-id hint store. CompanyDetailPage sets this on load so
 * the user-mutation helpers (which take only userId in the existing
 * signature) can build the correct route. A future refactor should change
 * the signatures to (tenantId, userId) and remove this.
 */
let _tenantHint: string | null = null;
export function setTenantHint(id: string | null): void { _tenantHint = id; }
function currentTenantHint(): string | null { return _tenantHint; }

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export function markInvoicePaid(tenantId: string): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(tenantId)}/billing/mark-paid`);
}

export function extendTrial(tenantId: string, days: number): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(tenantId)}/billing/extend-trial`, { days });
}

export function compTenant(tenantId: string, reason: string): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(tenantId)}/billing/comp`, { reason });
}

/**
 * Set (or clear) a tenant's custom monthly charge in ZAR cents. This overrides
 * the per-rep list price for this tenant — it's the amount the recurring debit
 * run collects — so it's how an operator locks in a negotiated price/discount.
 * Pass null to remove the override.
 */
export function setTenantMonthlyAmount(
  tenantId: string,
  monthlyAmountCents: number | null,
  reason?: string
): Promise<Tenant> {
  return post<Tenant>(`/v1/tenants/${encodeURIComponent(tenantId)}/billing/amount`, {
    monthlyAmountCents,
    ...(reason ? { reason } : {})
  });
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

/** Filters for the client access log (tenant plan phase 16). */
export interface ClientAccessFilters {
  clientTenantId?: string;
  supplierTenantId?: string;
  email?: string;
  surface?: string;
  refused?: boolean;
  from?: string;
  to?: string;
}

export interface ClientAccessRow {
  id: string;
  at: string;
  clientTenantId: string;
  clientName: string | null;
  supplierTenantId: string | null;
  supplierName: string | null;
  userId: string | null;
  userEmail: string | null;
  method: string;
  surface: string;
  targetId: string | null;
  status: number;
  nodeCompanyId: string | null;
  /** Someone at the reseller viewing the client's workspace as the client sees it (tenant plan phase 20). */
  viewedAs?: boolean;
}

export interface ClientAccessResult {
  items: ClientAccessRow[];
  bySurface: Array<{ surface: string; count: number; refused: number }>;
  limit: number;
}

export function fetchClientAccess(filters: ClientAccessFilters = {}): Promise<ClientAccessResult> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === "" || v === false) continue;
    params.set(k, v === true ? "1" : String(v));
  }
  const qs = params.toString();
  return get<ClientAccessResult>(`/v1/client-access${qs ? `?${qs}` : ""}`);
}

/** One client on Orbit's list of every reseller's clients (tenant plan phase 21). */
export interface ResellerClientRow {
  tenantId: string;
  name: string;
  resellerId: string;
  resellerName: string;
  enabled: boolean;
  /** Still the reseller's client; false when the reseller has let it go since. */
  current: boolean;
  /** Since when it has been live without a break; null while paused. */
  liveSince: string | null;
  /** Since when it has not been live (paused, or let go); null while live. */
  pausedSince: string | null;
  setUpAt: string;
  firstSignInAt: string | null;
  /** Days of the month it was live on, at any moment. */
  daysLive: number;
  people: number;
  joined: number;
  activePeople: number;
  lastActivityAt: string | null;
}

export interface ResellerClientTotals {
  tenantId: string;
  name: string;
  clients: number;
  liveNow: number;
  liveInMonth: number;
  /** Days live in the month, added up over its clients. */
  clientDays: number;
}

export interface ResellerClientRegister {
  month: string;
  from: string;
  to: string;
  resellers: ResellerClientTotals[];
  items: ResellerClientRow[];
}

/** Every reseller's clients for one month, YYYY-MM (this month without it). */
export function fetchResellerClients(month?: string): Promise<ResellerClientRegister> {
  return get<ResellerClientRegister>(`/v1/reseller-clients${month ? `?month=${encodeURIComponent(month)}` : ""}`);
}

export function fetchAudit(filter?: { tenantId?: string; types?: string[] }): Promise<AuditEvent[]> {
  const params = new URLSearchParams();
  if (filter?.tenantId) params.set("tenantId", filter.tenantId);
  if (filter?.types?.length) params.set("types", filter.types.join(","));
  const qs = params.toString();
  return get<AuditEvent[]>(`/v1/audit${qs ? `?${qs}` : ""}`);
}

// ---------------------------------------------------------------------------
// Impersonation
// ---------------------------------------------------------------------------

export async function startImpersonation(tenantId: string, reason: string): Promise<ImpersonationSession> {
  const imp = await post<ImpersonationSession>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/impersonate`,
    { reason }
  );
  updateSession({ impersonation: imp });
  return imp;
}

export async function endImpersonation(): Promise<void> {
  const session = loadSession();
  if (!session?.impersonation) return;
  try {
    await post<void>(
      `/v1/tenants/${encodeURIComponent(session.impersonation.tenantId)}/impersonate/end`
    );
  } catch {
    // The server-side audit row might fail if the tenant was deleted — clear the
    // banner regardless so the operator isn't stuck.
  }
  updateSession({ impersonation: null });
}

// ---------------------------------------------------------------------------
// Debit orders — stubbed at the API today (empty list, retry/mark-paid return
// 501). The UI surfaces "no data yet" until billing-service ships.
// ---------------------------------------------------------------------------

export function listDebitOrders(tenantId: string): Promise<DebitOrder[]> {
  return get<DebitOrder[]>(`/v1/tenants/${encodeURIComponent(tenantId)}/debit-orders`);
}

export function retryDebitOrder(orderId: string): Promise<DebitOrder> {
  const tenantId = currentTenantHint();
  if (!tenantId) throw new Error("Tenant context required");
  return post<DebitOrder>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/debit-orders/${encodeURIComponent(orderId)}/retry`
  );
}

export function markDebitOrderPaid(orderId: string, bankReference: string): Promise<DebitOrder> {
  const tenantId = currentTenantHint();
  if (!tenantId) throw new Error("Tenant context required");
  return post<DebitOrder>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/debit-orders/${encodeURIComponent(orderId)}/mark-paid`,
    { bankReference }
  );
}

// ---------------------------------------------------------------------------
// Tenant Twilio numbers — operator-side inventory + agent assignment. The
// customer app never sees these; assignment happens here only.
// ---------------------------------------------------------------------------

export interface TenantNumber {
  channel: string;
  externalId: string;
  settings: Record<string, unknown>;
  assignedAgentId: string | null;
  assignedAgentName: string | null;
}

export interface TenantSalesAgent {
  id: string;
  fullName: string;
  email?: string;
  phoneE164?: string;
  phoneVerifiedAt?: string;
  callerIdVerified?: boolean;
  status: string;
}

export function fetchTenantNumbers(tenantId: string): Promise<{ numbers: TenantNumber[] }> {
  return get<{ numbers: TenantNumber[] }>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers`);
}

export function fetchTenantSalesAgents(tenantId: string): Promise<{ agents: TenantSalesAgent[] }> {
  return get<{ agents: TenantSalesAgent[] }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/sales-agents`
  );
}

export function attachTenantNumbers(
  tenantId: string,
  numbers: string[],
  forwardTo?: string
): Promise<unknown> {
  return post<unknown>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers/attach`, {
    numbers,
    ...(forwardTo ? { forwardTo } : {})
  });
}

export function assignTenantNumber(
  tenantId: string,
  inboundNumber: string,
  agentId: string
): Promise<unknown> {
  return post<unknown>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers/assign`, {
    inboundNumber,
    agentId
  });
}

export function unassignTenantNumber(tenantId: string, inboundNumber: string): Promise<unknown> {
  return post<unknown>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers/unassign`, {
    inboundNumber
  });
}

export interface AvailableNumber {
  phoneNumber: string;
  friendlyName?: string;
  locality?: string;
  region?: string;
  isoCountry?: string;
}

/** Search Twilio for buyable numbers. Read-only, spends nothing. */
export function searchAvailableNumbers(
  tenantId: string,
  opts: { country?: string; areaCode?: string } = {}
): Promise<{ numbers: AvailableNumber[] }> {
  const qs = new URLSearchParams();
  if (opts.country) qs.set("country", opts.country);
  if (opts.areaCode) qs.set("areaCode", opts.areaCode);
  const suffix = qs.toString() ? `?${qs}` : "";
  return get<{ numbers: AvailableNumber[] }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/numbers/available${suffix}`
  );
}

/**
 * Buy a number and wire it to the tenant in one call: purchase, point it at the
 * voice webhook, and create the routing row. Replaces "go to the Twilio console,
 * buy it there, come back and hit Sync" - the manual step that made getting a
 * number a one-business-day wait.
 *
 * Spends money on a recurring basis, so it is operator-only and one at a time.
 */
export function provisionTenantNumber(
  tenantId: string,
  input: { number: string; country?: string; forwardTo?: string; force?: boolean }
): Promise<unknown> {
  return post<unknown>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers/provision`, input);
}

/** Pull numbers bought in the tenant's Twilio subaccount into Orbit + wire their webhooks. */
export function syncTenantNumbers(
  tenantId: string
): Promise<{ synced: string[]; configured: string[]; note?: string }> {
  return post<{ synced: string[]; configured: string[]; note?: string }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/numbers/sync`,
    {}
  );
}

/** Detach a number from the tenant (removes Orbit routing; does NOT release it on Twilio). */
export function detachTenantNumber(
  tenantId: string,
  number: string,
  channel = "voice"
): Promise<{ detached: string }> {
  return post<{ detached: string }>(`/v1/tenants/${encodeURIComponent(tenantId)}/numbers/detach`, {
    number,
    channel
  });
}

/** Release a number: delete it from the tenant's Twilio subaccount + remove its Orbit routing. */
export function releaseTenantNumber(
  tenantId: string,
  number: string
): Promise<{ number: string; released: boolean }> {
  return post<{ number: string; released: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/numbers/release`,
    { number }
  );
}

/** Start Twilio caller-ID verification for a rep (Twilio calls them; they key in the returned code). */
export function verifyAgentCallerId(
  tenantId: string,
  agentId: string
): Promise<{ validationCode?: string; alreadyVerified?: boolean; verified?: boolean }> {
  return post<{ validationCode?: string; alreadyVerified?: boolean; verified?: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/sales-agents/${encodeURIComponent(agentId)}/caller-id/verify`,
    {}
  );
}

/** Confirm caller-ID verification once the rep has keyed in the code. */
export function confirmAgentCallerId(tenantId: string, agentId: string): Promise<{ verified: boolean }> {
  return post<{ verified: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/sales-agents/${encodeURIComponent(agentId)}/caller-id/confirm`,
    {}
  );
}

/** Reset caller-ID verification (delete the verified caller ID + clear flags) so the verify flow can be re-run. */
export function resetAgentCallerId(tenantId: string, agentId: string): Promise<{ reset: boolean }> {
  return post<{ reset: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/sales-agents/${encodeURIComponent(agentId)}/caller-id/reset`,
    {}
  );
}

/** One-click Twilio subaccount: created under the master account, SID saved on the tenant. */
export function createTenantSubaccount(
  tenantId: string
): Promise<{ tenant: Tenant; subaccount: { sid: string; mock?: boolean } }> {
  return post<{ tenant: Tenant; subaccount: { sid: string; mock?: boolean } }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/twilio-subaccount`
  );
}

// ---------------------------------------------------------------------------
// Tenant WhatsApp (Coexistence) — operator launches Meta's Embedded Signup for
// a chosen sales agent in the browser, then posts the resulting { code, wabaId }
// here so the backend can exchange the code, register the phone number and wire
// it to the agent. Listing returns the WhatsApp senders already connected.
// ---------------------------------------------------------------------------

export interface TenantWhatsAppIntegration {
  phoneNumberId: string;
  displayPhoneNumber: string;
  assignedAgentId: string | null;
  status: string;
  [key: string]: unknown;
}

export function getTenantWhatsApp(
  tenantId: string
): Promise<{ integrations: TenantWhatsAppIntegration[] }> {
  return get<{ integrations: TenantWhatsAppIntegration[] }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/whatsapp`
  );
}

export function connectTenantWhatsApp(
  tenantId: string,
  payload: { agentId: string; code: string; wabaId: string }
): Promise<{ status: string; phoneNumberId: string; displayPhoneNumber: string }> {
  return post<{ status: string; phoneNumberId: string; displayPhoneNumber: string }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/whatsapp/connect`,
    payload
  );
}

// ---------------------------------------------------------------------------
// Usage — real per-tenant breakdown from analytics (proxied by the admin
// service at /v1/finance/tenants/:id/usage). Voice + WhatsApp carry per-day and
// per-agent resolution (analytics_events); AI tokens + email sends only exist
// at MONTHLY resolution (usage_monthly), so they appear in the integration
// totals but not in the daily trend. Costs = units × COST_RATES (COGS).
// ---------------------------------------------------------------------------

export function resolveRange(range: UsageRange | { start: string; end: string }): { start: Date; end: Date } {
  if (typeof range !== "string") return { start: new Date(range.start), end: new Date(range.end) };
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));
  const start = new Date(end);
  switch (range) {
    case "7d":  start.setUTCDate(start.getUTCDate() - 6);  start.setUTCHours(0, 0, 0, 0); return { start, end };
    case "30d": start.setUTCDate(start.getUTCDate() - 29); start.setUTCHours(0, 0, 0, 0); return { start, end };
    case "this_month": {
      const s = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return { start: s, end };
    }
    case "last_month": {
      const s = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const e = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59));
      return { start: s, end: e };
    }
    case "ytd": {
      const s = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      return { start: s, end };
    }
  }
}

function emptyBreakdown(start: Date, end: Date): CostBreakdown {
  const integrations: IntegrationKey[] = ["openai", "twilio_voice", "twilio_recording", "twilio_whatsapp", "sendgrid"];
  const days: { day: string; cents: number; byIntegration: Record<IntegrationKey, number> }[] = [];
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setUTCDate(d.getUTCDate() + 1)) {
    days.push({
      day: d.toISOString().slice(0, 10),
      cents: 0,
      byIntegration: { openai: 0, twilio_voice: 0, twilio_recording: 0, twilio_whatsapp: 0, sendgrid: 0 }
    });
  }
  return {
    rangeStart: start.toISOString(),
    rangeEnd: end.toISOString(),
    totalCents: 0,
    byIntegration: integrations.map((i) => ({ integration: i, units: 0, cents: 0 })),
    byEmployee: [],
    byDay: days
  };
}

interface ServerUsageBreakdown {
  rangeStart: string;
  rangeEnd: string;
  byDay: Array<{
    day: string;
    voiceMinutes: number;
    whatsappMessages: number;
    aiTokens?: number;
    emailSends?: number;
  }>;
  byEmployee: Array<{
    agentId: string | null;
    agentName: string;
    voiceMinutes: number;
    whatsappMessages: number;
    events: number;
  }>;
  totals: { voiceMinutes: number; whatsappMessages: number; aiTokens: number; emailSends: number };
}

function toCostBreakdown(server: ServerUsageBreakdown, start: Date, end: Date): CostBreakdown {
  const base = emptyBreakdown(start, end);
  const dayIndex = new Map(base.byDay.map((d) => [d.day, d]));
  for (const d of server.byDay) {
    const row = dayIndex.get(d.day);
    if (!row) continue;
    row.byIntegration.openai = costCents("openai", d.aiTokens ?? 0);
    row.byIntegration.twilio_voice = costCents("twilio_voice", d.voiceMinutes);
    row.byIntegration.twilio_whatsapp = costCents("twilio_whatsapp", d.whatsappMessages);
    row.byIntegration.sendgrid = costCents("sendgrid", d.emailSends ?? 0);
    row.cents =
      row.byIntegration.openai +
      row.byIntegration.twilio_voice +
      row.byIntegration.twilio_whatsapp +
      row.byIntegration.sendgrid;
  }
  const byIntegration: CostBreakdown["byIntegration"] = [
    { integration: "openai", units: server.totals.aiTokens, cents: costCents("openai", server.totals.aiTokens) },
    { integration: "twilio_voice", units: server.totals.voiceMinutes, cents: costCents("twilio_voice", server.totals.voiceMinutes) },
    { integration: "twilio_recording", units: 0, cents: 0 },
    { integration: "twilio_whatsapp", units: server.totals.whatsappMessages, cents: costCents("twilio_whatsapp", server.totals.whatsappMessages) },
    { integration: "sendgrid", units: server.totals.emailSends, cents: costCents("sendgrid", server.totals.emailSends) }
  ];
  return {
    ...base,
    totalCents: byIntegration.reduce((s, r) => s + r.cents, 0),
    byIntegration,
    byEmployee: server.byEmployee.map((e) => ({
      userId: e.agentId,
      userName: e.agentName,
      events: e.events,
      cents: costCents("twilio_voice", e.voiceMinutes) + costCents("twilio_whatsapp", e.whatsappMessages)
    }))
  };
}

async function fetchUsageBreakdown(
  tenantId: string,
  range: UsageRange | { start: string; end: string },
  agentId?: string
): Promise<{ events: UsageEvent[]; breakdown: CostBreakdown }> {
  const { start, end } = resolveRange(range);
  const qs = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
  if (agentId) qs.set("agentId", agentId);
  try {
    const server = await get<ServerUsageBreakdown>(
      `/v1/finance/tenants/${encodeURIComponent(tenantId)}/usage?${qs.toString()}`
    );
    return { events: [], breakdown: toCostBreakdown(server, start, end) };
  } catch {
    // Analytics unreachable — render zeros rather than breaking the tab.
    return { events: [], breakdown: emptyBreakdown(start, end) };
  }
}

export async function fetchTenantUsage(
  tenantId: string,
  range: UsageRange | { start: string; end: string }
): Promise<{ events: UsageEvent[]; breakdown: CostBreakdown }> {
  return fetchUsageBreakdown(tenantId, range);
}

export async function fetchEmployeeUsage(
  tenantId: string,
  userId: string,
  range: UsageRange | { start: string; end: string }
): Promise<{ events: UsageEvent[]; breakdown: CostBreakdown }> {
  return fetchUsageBreakdown(tenantId, range, userId);
}

// ---------------------------------------------------------------------------
// Finance — revenue vs costs vs profit for THIS environment, plus the fixed
// infra cost line items (AWS boxes etc.) allocated across paying customers.
// ---------------------------------------------------------------------------

export type InfraCategory = "aws" | "network" | "ai" | "comms" | "tooling" | "other";

export interface InfraCostItem {
  id: string;
  environment: string;
  name: string;
  category: InfraCategory;
  amountCents: number;
  currency: "ZAR" | "USD";
  zarCents: number;
  notes: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceCogs {
  openaiCents: number;
  voiceCents: number;
  whatsappCents: number;
  emailCents: number;
  costCents: number;
}

export interface FinanceSummary {
  environment: string;
  month: string;
  isCurrentMonth: boolean;
  cogsKnown: boolean;
  customers: { total: number; active: number; paying: number; allocationDivisor: number };
  revenue: {
    mrrCents: number;
    billedCents: number;
    collectedCents: number;
    creditPurchaseCents: number;
    overageBilledCents: number;
    totalBilledCents: number;
    totalCollectedCents: number;
  };
  costs: {
    cogs: FinanceCogs;
    aiSpendZarCents: number | null;
    infraZarCents: number;
    infraPerCustomerCents: number;
    totalCents: number;
  };
  profit: {
    billedBasisCents: number;
    collectedBasisCents: number;
    marginPct: number | null;
  };
  infraCosts: InfraCostItem[];
}

export interface FinanceTenantRow {
  tenantId: string;
  name: string;
  pack: string | null;
  plan: string;
  status: string;
  billingStatus: string;
  paymentProvider: string | null;
  mandateStatus: string | null;
  seats: number;
  perSeatCents: number;
  monthlyAmountCents: number;
  paying: boolean;
  collectedCents: number;
  collectionsCount: number;
  failedCollectionsCount: number;
  lastSuccessfulCollectionAt: string | null;
  creditPurchaseCents: number;
  overageBilledCents: number;
  usage: { voiceMinutes: number; whatsappMessages: number; aiTokens: number; emailSends: number };
  cogs: FinanceCogs;
  aiSpendZarCents: number | null;
  allocatedInfraCents: number;
  revenueCents: number;
  totalCostCents: number;
  profitCents: number;
  marginPct: number | null;
}

export function fetchFinanceSummary(month?: string): Promise<FinanceSummary> {
  return get<FinanceSummary>(`/v1/finance/summary${month ? `?month=${encodeURIComponent(month)}` : ""}`);
}

export function fetchFinanceTenants(
  month?: string
): Promise<{ month: string; environment: string; cogsKnown: boolean; tenants: FinanceTenantRow[] }> {
  return get(`/v1/finance/tenants${month ? `?month=${encodeURIComponent(month)}` : ""}`);
}

export function createInfraCost(input: {
  name: string;
  category: InfraCategory;
  amountCents: number;
  currency: "ZAR" | "USD";
  notes?: string;
}): Promise<{ cost: InfraCostItem }> {
  return post(`/v1/finance/infra-costs`, input);
}

export function updateInfraCost(
  id: string,
  patchBody: Partial<{
    name: string;
    category: InfraCategory;
    amountCents: number;
    currency: "ZAR" | "USD";
    notes: string;
    active: boolean;
  }>
): Promise<{ cost: InfraCostItem }> {
  return patch(`/v1/finance/infra-costs/${encodeURIComponent(id)}`, patchBody);
}

export function deleteInfraCost(id: string): Promise<{ ok: true }> {
  return del(`/v1/finance/infra-costs/${encodeURIComponent(id)}`);
}

export interface AwsSyncResult {
  status: "ok" | "access-denied" | "no-credentials" | "error";
  message?: string;
  monthToDateUsd?: number;
  projectedMonthUsd?: number;
  shareFraction?: number;
  bookedUsd?: number;
}

/** Pull month-to-date AWS spend from Cost Explorer and re-book this env's share. */
export function syncAwsCosts(): Promise<AwsSyncResult> {
  return post(`/v1/finance/infra-costs/sync-aws`);
}

// ---------------------------------------------------------------------------
// Provider credit monitor — live Twilio balance + AI budget-remaining, with
// warn/error thresholds; plus the platform's usage-per-day feed.
// ---------------------------------------------------------------------------

export type ProviderStatusLevel = "ok" | "warning" | "error" | "unknown";

export interface ProviderStatus {
  id: string;
  provider: string;
  label: string;
  kind: "balance_api" | "budget";
  currency: string;
  remainingCents: number | null;
  remainingZarCents: number | null;
  spendCents: number | null;
  monthlyBudgetCents: number | null;
  warnThresholdCents: number;
  errorThresholdCents: number;
  status: ProviderStatusLevel;
  detail: string;
  active: boolean;
}

export function fetchProviderStatuses(): Promise<{ providers: ProviderStatus[] }> {
  return get(`/v1/finance/providers`);
}

export function updateProviderAccount(
  id: string,
  patchBody: Partial<{
    label: string;
    monthlyBudgetCents: number | null;
    warnThresholdCents: number;
    errorThresholdCents: number;
    active: boolean;
  }>
): Promise<{ providers: ProviderStatus[] }> {
  return patch(`/v1/finance/providers/${encodeURIComponent(id)}`, patchBody);
}

export interface DailyUsageRow {
  day: string;
  voiceMinutes: number;
  whatsappMessages: number;
  aiTokens: number;
  emailSends: number;
}

export function fetchDailyUsage(start: string, end: string): Promise<{ days: DailyUsageRow[] }> {
  const qs = new URLSearchParams({ start, end });
  return get(`/v1/finance/usage-daily?${qs.toString()}`);
}

// ---------------------------------------------------------------------------
// Remote config / feature flags (per environment, non-secret)
// ---------------------------------------------------------------------------

export interface RemoteFlag {
  key: string;
  type: "boolean" | "number" | "string" | "enum";
  category: string;
  description: string;
  value: string;
  default: string;
  options?: string[];
  /** Human labels for enum options, keyed by value (falls back to the value). */
  optionLabels?: Record<string, string>;
  isOverridden: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface RemoteConfigResponse {
  environment: string;
  environments: string[];
  items: RemoteFlag[];
}

export function fetchRemoteConfig(environment?: string): Promise<RemoteConfigResponse> {
  const qs = environment ? `?environment=${encodeURIComponent(environment)}` : "";
  return get<RemoteConfigResponse>(`/v1/remote-config${qs}`);
}

export function setRemoteFlag(key: string, value: string, environment?: string): Promise<{ ok: boolean }> {
  const qs = environment ? `?environment=${encodeURIComponent(environment)}` : "";
  return put<{ ok: boolean }>(`/v1/remote-config/${encodeURIComponent(key)}${qs}`, { value });
}

export function resetRemoteFlag(key: string, environment?: string): Promise<{ ok: boolean }> {
  const qs = environment ? `?environment=${encodeURIComponent(environment)}` : "";
  return put<{ ok: boolean }>(`/v1/remote-config/${encodeURIComponent(key)}${qs}`, { reset: true });
}

// ---------------------------------------------------------------------------
// Per-tenant feature flags. Distinct from the block above, which is the
// per-ENVIRONMENT config: this is one company's overrides, and it is the only
// way to give a single tenant a module that is deliberately in no package.
// ---------------------------------------------------------------------------

export interface TenantFlag {
  key: string;
  type: "boolean" | "number" | "string" | "enum";
  category: string;
  description: string;
  /** The raw winning value, as a string. */
  value: string;
  /** The same value, typed. */
  effective: boolean | number | string;
  default: string;
  options?: string[];
  /** Where the winning value came from (most specific wins). */
  source: "tenant" | "pack" | "environment" | "default";
  isTenantOverride: boolean;
}

export interface TenantRemoteConfig {
  environment: string;
  tenantId: string;
  pack: string | null;
  items: TenantFlag[];
}

export function fetchTenantFlags(tenantId: string): Promise<TenantRemoteConfig> {
  return get<TenantRemoteConfig>(`/v1/tenants/${encodeURIComponent(tenantId)}/remote-config`);
}

export function setTenantFlag(tenantId: string, key: string, value: string): Promise<{ ok: boolean }> {
  return put<{ ok: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/remote-config/${encodeURIComponent(key)}`,
    { value }
  );
}

/** Drop the override so the tenant falls back to whatever their package says. */
export function resetTenantFlag(tenantId: string, key: string): Promise<{ ok: boolean }> {
  return put<{ ok: boolean }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/remote-config/${encodeURIComponent(key)}`,
    { reset: true }
  );
}

// ---------------------------------------------------------------------------
// App releases — the Android APK catalog. Operators publish a new build here;
// it proxies through platform-admin-service to config-service (which owns the
// binaries + metadata). Publishing is multipart/form-data (the APK is a blob),
// so it does NOT go through the JSON `post` helper — we hand the browser a
// FormData and let it set the multipart Content-Type (with its boundary).
// ---------------------------------------------------------------------------

export type AppReleaseStatus = "active" | "deprecated" | "blocked";

/** The three phone apps, each with its own release line (tenant plan phase 57). */
export type AppId = "sales" | "inventory" | "tenant";
export const APP_CHOICES: Array<{ id: AppId; label: string }> = [
  { id: "sales", label: "Orbit Sales" },
  { id: "inventory", label: "Orbit Inventory" },
  { id: "tenant", label: "Orbit Tenant" }
];

export interface AppRelease {
  /** Which app this build is. Older config-service responses leave it out: those are Sales. */
  app?: AppId;
  platform: string;
  versionCode: number;
  versionName: string;
  releaseNotes: string;
  sizeBytes: number;
  sha256: string;
  status: AppReleaseStatus;
  mandatory: boolean;
  publishedBy: string | null;
  publishedAt: string;
  /** Root-relative path the phone app resolves against its own gateway base. */
  apkUrl: string;
  /**
   * Absolute, PUBLIC download URL added by platform-admin-service. /api/app/* is
   * unauthenticated at the gateway by design, so this link can be sent straight
   * to a rep's phone — no login, no VPN. Undefined only if config-service
   * returned no apkUrl.
   */
  publicApkUrl?: string;
}

export function listAppReleases(platform = "android", app: AppId = "sales"): Promise<{ releases: AppRelease[] }> {
  return get<{ releases: AppRelease[] }>(
    `/v1/app-releases?platform=${encodeURIComponent(platform)}&app=${encodeURIComponent(app)}`
  );
}

/**
 * Publish a new release. Takes a ready-built FormData (apk file + versionCode,
 * versionName, releaseNotes, mandatory, and optionally platform/publishedBy).
 * Auth still travels via the HttpOnly cookie (credentials: "include"); we never
 * set Content-Type so the browser adds the correct multipart boundary. Errors —
 * including the 409 "version already exists" — surface as ApiError with the
 * config-service message.
 */
export async function publishAppRelease(form: FormData): Promise<AppRelease> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/app-releases`, {
      method: "POST",
      body: form,
      credentials: "include"
      // No Content-Type header: the browser sets multipart/form-data; boundary=…
    });
  } catch {
    throw new ApiError(0, "Can't reach the admin service. Check your connection and try again.");
  }
  const text = await res.text();
  let body: unknown = null;
  if (text) { try { body = JSON.parse(text); } catch { body = null; } }
  if (!res.ok) {
    const err = (body && typeof body === "object" ? body : null) as
      | { error?: string; message?: string; code?: string }
      | null;
    const message = err?.error ?? err?.message ?? messageForStatus(res.status);
    throw new ApiError(res.status, message, err?.code);
  }
  return body as AppRelease;
}

export function updateAppRelease(
  app: AppId,
  platform: string,
  versionCode: number,
  patchInput: { status?: AppReleaseStatus; mandatory?: boolean; releaseNotes?: string }
): Promise<AppRelease> {
  return patch<AppRelease>(
    `/v1/app-releases/${encodeURIComponent(app)}/${encodeURIComponent(platform)}/${encodeURIComponent(versionCode)}`,
    patchInput
  );
}

/** One crash uploaded by a phone's uncaught-exception handler. */
export interface AppCrash {
  id: string;
  /** Which app crashed; older rows are Sales. */
  app?: AppId;
  platform: string;
  versionCode?: number;
  versionName: string;
  device: string;
  osVersion: string;
  userEmail?: string;
  tenantId?: string;
  threadName?: string;
  summary: string;
  stack: string;
  crashedAt?: string;
  createdAt: string;
}

export function listAppCrashes(limit = 200): Promise<{ crashes: AppCrash[] }> {
  return get<{ crashes: AppCrash[] }>(`/v1/app-releases/crashes?limit=${limit}`);
}

// ---------------------------------------------------------------------------
// Support tickets — operator-facing board. Customers raise tickets in their
// app; operators triage/resolve them here. The original request is in
// `ticket.body`; `messages` carries the replies (and operator-only internal
// notes). Moving a ticket to "done" requires a non-empty resolution.
// ---------------------------------------------------------------------------

export function listSupportTickets(
  /** `unread` left undefined means both — only true/false narrows the board. */
  filters?: { businessId?: string; status?: TicketStatus; q?: string; unread?: boolean }
): Promise<{ items: TicketSummary[] }> {
  const params = new URLSearchParams();
  if (filters?.businessId) params.set("businessId", filters.businessId);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.q) params.set("q", filters.q);
  if (filters?.unread !== undefined) params.set("unread", String(filters.unread));
  const qs = params.toString();
  return get<{ items: TicketSummary[] }>(`/v1/support${qs ? `?${qs}` : ""}`);
}

export function getSupportTicket(
  id: string
): Promise<{ ticket: TicketDetail; messages: TicketMessage[]; company: { id: string; name: string } }> {
  return get<{ ticket: TicketDetail; messages: TicketMessage[]; company: { id: string; name: string } }>(
    `/v1/support/${encodeURIComponent(id)}`
  );
}

export function updateSupportTicket(
  id: string,
  patchInput: {
    status?: TicketStatus;
    priority?: TicketPriority;
    assignedOperator?: string | null;
    resolution?: string;
  }
): Promise<{ ticket: TicketDetail }> {
  return patch<{ ticket: TicketDetail }>(`/v1/support/${encodeURIComponent(id)}`, patchInput);
}

/**
 * Change a ticket's status. When moving to "done" the backend requires a
 * non-empty resolution (it 400s otherwise), so the UI prompts for one first.
 */
export function changeSupportStatus(
  id: string,
  status: TicketStatus,
  resolution?: string
): Promise<{ ticket: TicketDetail }> {
  const body: { status: TicketStatus; resolution?: string } = { status };
  if (resolution !== undefined) body.resolution = resolution;
  return patch<{ ticket: TicketDetail }>(`/v1/support/${encodeURIComponent(id)}`, body);
}

export function postSupportMessage(
  id: string,
  body: string,
  internal?: boolean
  // `status` is where the ticket ended up: a public reply hands it to the
  // customer, so the server moves it to Client review. Unchanged for an
  // internal note, and a closed ticket stays closed.
): Promise<{ message: TicketMessage; status: TicketStatus }> {
  return post<{ message: TicketMessage; status: TicketStatus }>(`/v1/support/${encodeURIComponent(id)}/messages`, {
    body,
    ...(internal ? { internal: true } : {})
  });
}

/** Fetch attachments for a ticket with presigned S3 download URLs. */
export function listSupportAttachments(id: string): Promise<{ attachments: TicketAttachment[] }> {
  return get(`/v1/support/${encodeURIComponent(id)}/attachments`);
}

// ---------------------------------------------------------------------------
// Onboarding discovery + migration requests — operator board. Self-service
// signups fill in a short discovery step (current CRM, migrate?, dislikes) when
// they create their workspace; operators triage the migration requests here.
// ---------------------------------------------------------------------------

export function listMigrations(
  filters?: { status?: MigrationStatus; q?: string }
): Promise<{ items: DiscoveryRecord[] }> {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.q) params.set("q", filters.q);
  const qs = params.toString();
  return get<{ items: DiscoveryRecord[] }>(`/v1/migrations${qs ? `?${qs}` : ""}`);
}

export function updateMigration(
  businessId: string,
  patchInput: {
    migrationStatus?: MigrationStatus;
    note?: string | null;
    /** Runbook step flags to merge (step key -> done). */
    runbook?: Record<string, boolean>;
  }
): Promise<{ discovery: DiscoveryRecord }> {
  return patch<{ discovery: DiscoveryRecord }>(
    `/v1/migrations/${encodeURIComponent(businessId)}`,
    patchInput
  );
}

export interface FunnelEvent {
  event: string;
  /** Distinct tenants that hit this event. */
  tenants: number;
  /** Raw event volume. */
  total: number;
}

/** Onboarding funnel counts over the last `days` (default 90). */
export function getOnboardingFunnel(days?: number): Promise<{ sinceDays: number; events: FunnelEvent[] }> {
  const qs = days ? `?days=${days}` : "";
  return get<{ sinceDays: number; events: FunnelEvent[] }>(`/v1/onboarding-funnel${qs}`);
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

/** Close to a real ceiling. People never count: seats are unlimited. */
export function isNearLimit(t: Tenant): boolean {
  const u = t.usage;
  const l = t.effectiveLimits;
  const fractions = [
    u.numbers / l.maxNumbers,
    u.customers / l.maxCustomers,
    u.voiceMinutesThisMonth / l.voiceMinutesPerMonth,
    u.whatsappMessagesThisMonth / l.whatsappMessagesPerMonth,
    u.aiTokensThisMonth / l.aiTokensPerMonth
  ];
  return fractions.some((r) => Number.isFinite(r) && r >= 0.8);
}

// ---------------------------------------------------------------------------
// Referral / partner program — operator-facing CRUD + pipeline + leaderboard.
// ---------------------------------------------------------------------------

export function listReferrers(): Promise<{ items: ReferrerPartner[] }> {
  return get<{ items: ReferrerPartner[] }>("/v1/referrers");
}

export function createReferrer(input: {
  email: string;
  fullName: string;
  phone?: string | null;
  company?: string | null;
  commissionRatePct?: number;
  commissionDurationMonths?: number | null;
  notes?: string | null;
}): Promise<{ referrer: ReferrerPartner }> {
  return post<{ referrer: ReferrerPartner }>("/v1/referrers", input);
}

export function getReferrer(
  id: string
): Promise<{ referrer: ReferrerPartner; referrals: ReferralSummary[] }> {
  return get<{ referrer: ReferrerPartner; referrals: ReferralSummary[] }>(
    `/v1/referrers/${encodeURIComponent(id)}`
  );
}

export function updateReferrer(
  id: string,
  patchInput: {
    fullName?: string;
    phone?: string | null;
    company?: string | null;
    status?: ReferrerStatus;
    commissionRatePct?: number;
    commissionDurationMonths?: number | null;
    notes?: string | null;
  }
): Promise<{ referrer: ReferrerPartner }> {
  return patch<{ referrer: ReferrerPartner }>(`/v1/referrers/${encodeURIComponent(id)}`, patchInput);
}

export async function deleteReferrer(id: string): Promise<void> {
  await del<{ ok: boolean }>(`/v1/referrers/${encodeURIComponent(id)}`);
}

export function listReferrals(filters?: {
  status?: ReferralStatus;
  referrerId?: string;
  q?: string;
}): Promise<{ items: ReferralSummary[] }> {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.referrerId) params.set("referrerId", filters.referrerId);
  if (filters?.q) params.set("q", filters.q);
  const qs = params.toString();
  return get<{ items: ReferralSummary[] }>(`/v1/referrals${qs ? `?${qs}` : ""}`);
}

export function getReferral(
  id: string
): Promise<{ referral: ReferralDetail; updates: ReferralUpdate[] }> {
  return get<{ referral: ReferralDetail; updates: ReferralUpdate[] }>(
    `/v1/referrals/${encodeURIComponent(id)}`
  );
}

export function createReferralAsOperator(input: {
  referrerId: string;
  prospectCompany: string;
  prospectContactName?: string | null;
  prospectEmail?: string | null;
  prospectPhone?: string | null;
  notes?: string | null;
}): Promise<{ referral: ReferralDetail; conflict: boolean }> {
  return post<{ referral: ReferralDetail; conflict: boolean }>("/v1/referrals", input);
}

export function updateReferral(
  id: string,
  patchInput: {
    status?: ReferralStatus;
    prospectCompany?: string;
    prospectContactName?: string | null;
    prospectEmail?: string | null;
    prospectPhone?: string | null;
    notes?: string | null;
    monthlyValueCents?: number | null;
    commissionRatePct?: number | null;
    clearConflict?: boolean;
  }
): Promise<{ referral: ReferralDetail }> {
  return patch<{ referral: ReferralDetail }>(`/v1/referrals/${encodeURIComponent(id)}`, patchInput);
}

export function assignReferral(
  id: string,
  input: {
    businessId: string;
    monthlyValueCents?: number;
    commissionRatePct?: number;
    commissionDurationMonths?: number | null;
  }
): Promise<{ referral: ReferralDetail }> {
  return post<{ referral: ReferralDetail }>(`/v1/referrals/${encodeURIComponent(id)}/assign`, input);
}

export function fetchReferralLeaderboard(): Promise<{ leaderboard: ReferrerLeaderboardEntry[] }> {
  return get<{ leaderboard: ReferrerLeaderboardEntry[] }>("/v1/referrals/leaderboard");
}

export interface ServiceHealth {
  ok: boolean;
  url: string;
  latencyMs: number;
  error?: string;
}

export interface SystemHealthResponse {
  ok: boolean;
  checkedAt: string;
  deploying: boolean;
  runUrl?: string;
  services: Record<string, ServiceHealth>;
}

export function getSystemHealth(): Promise<SystemHealthResponse> {
  return get<SystemHealthResponse>("/v1/system-health");
}

// ---------------------------------------------------------------------------
// Service logging
// ---------------------------------------------------------------------------

export interface LogEntry {
  id: string;
  ts: string;
  level: "debug" | "info" | "warn" | "error" | "critical";
  service: string;
  message: string;
  tenantId?: string;
  userId?: string;
  requestId?: string;
  meta?: Record<string, unknown>;
}

export interface GetLogsParams {
  services?: string[];
  levels?: string[];
  search?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}

export function getLogs(params: GetLogsParams = {}): Promise<{ items: LogEntry[]; total: number }> {
  const qs = new URLSearchParams();
  (params.services ?? []).forEach((s) => qs.append("service", s));
  (params.levels ?? []).forEach((l) => qs.append("level", l));
  if (params.search) qs.set("search", params.search);
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  return get<{ items: LogEntry[]; total: number }>(`/v1/logs${query ? `?${query}` : ""}`);
}

/* ------------------------------------------------------------------------- */
/* Mission Control board                                                       */
/*                                                                             */
/* The operator work queue behind /v1/ops (migration 027). Every mutation is    */
/* single-card and server-side, so a drag in the UI and a transition from an    */
/* agent can land at the same moment without either overwriting the other. This */
/* replaced /v1/board, which PUT the whole document on every change and so      */
/* silently lost concurrent edits.                                             */
/* ------------------------------------------------------------------------- */

export function getOpsBoard(): Promise<OpsBoardResponse> {
  return get<OpsBoardResponse>("/v1/ops/board");
}

export function listOpsCards(filters?: { stream?: string; col?: OpsColumn; q?: string }): Promise<{ items: OpsCard[] }> {
  const params = new URLSearchParams();
  if (filters?.stream) params.set("stream", filters.stream);
  if (filters?.col) params.set("col", filters.col);
  if (filters?.q) params.set("q", filters.q);
  const qs = params.toString();
  return get<{ items: OpsCard[] }>(`/v1/ops/cards${qs ? `?${qs}` : ""}`);
}

export function getOpsCard(ref: string): Promise<{ card: OpsCard; events: OpsEvent[] }> {
  return get<{ card: OpsCard; events: OpsEvent[] }>(`/v1/ops/cards/${encodeURIComponent(ref)}`);
}

export function createOpsCard(input: {
  streamId: string;
  title: string;
  body?: string;
  col?: OpsColumn;
  priority?: number;
  labels?: string[];
  acceptance?: AcceptanceCriterion[] | null;
  filesInScope?: string[] | null;
  testPlan?: string | null;
  source?: string;
  planRef?: string | null;
  branch?: string | null;
  prUrl?: string | null;
}): Promise<{ card: OpsCard }> {
  return post<{ card: OpsCard }>("/v1/ops/cards", input);
}

export function updateOpsCard(
  ref: string,
  changes: Partial<{
    title: string; body: string; priority: number; labels: string[];
    acceptance: AcceptanceCriterion[] | null; filesInScope: string[] | null;
    testPlan: string | null; branch: string | null; prUrl: string | null;
    ciStatus: string | null; planRef: string | null; streamId: string;
  }>
): Promise<{ card: OpsCard }> {
  return patch<{ card: OpsCard }>(`/v1/ops/cards/${encodeURIComponent(ref)}`, changes);
}

export function moveOpsCard(
  ref: string, to: OpsColumn, note?: string, viaUi?: boolean
): Promise<{ card: OpsCard }> {
  return post<{ card: OpsCard }>(`/v1/ops/cards/${encodeURIComponent(ref)}/move`, {
    to, ...(note ? { note } : {}), ...(viaUi ? { viaUi: true } : {})
  });
}

export function commentOpsCard(ref: string, text: string): Promise<{ card: OpsCard }> {
  return post<{ card: OpsCard }>(`/v1/ops/cards/${encodeURIComponent(ref)}/comment`, { text });
}

export function deleteOpsCard(ref: string): Promise<void> {
  return del<void>(`/v1/ops/cards/${encodeURIComponent(ref)}`);
}

/** Fleet kill switch. Ships paused; nothing claims a ticket while this is true. */
export function setOpsBoardPaused(paused: boolean): Promise<{ paused: boolean }> {
  return post<{ paused: boolean }>("/v1/ops/pause", { paused });
}

export function setOpsStreamPaused(id: string, paused: boolean): Promise<{ id: string; paused: boolean }> {
  return patch<{ id: string; paused: boolean }>(`/v1/ops/streams/${encodeURIComponent(id)}`, { paused });
}

/** Return tickets stranded by a crashed worker to the queue. */
export function sweepOpsLeases(): Promise<{ recovered: string[] }> {
  return post<{ recovered: string[] }>("/v1/ops/sweep", {});
}

export function listOpsEvents(since?: string, limit?: number): Promise<{ items: OpsEvent[] }> {
  const params = new URLSearchParams();
  if (since) params.set("since", since);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return get<{ items: OpsEvent[] }>(`/v1/ops/events${qs ? `?${qs}` : ""}`);
}

/* ------------------------------------------------------------------------- */
/* Mission Control panel                                                       */
/*                                                                             */
/* The fleet's own record behind /v1/ops/control (migration 028): the roster,  */
/* what each agent was told to do, what it actually did, who hands work to      */
/* whom, and the conversation with the chief of staff.                          */
/* ------------------------------------------------------------------------- */

export function getControlPanel(): Promise<ControlPanelResponse> {
  return get<ControlPanelResponse>("/v1/ops/control/panel");
}

export function listOpsAgents(): Promise<{ items: OpsAgent[] }> {
  return get<{ items: OpsAgent[] }>("/v1/ops/control/agents");
}

export function getOpsAgent(id: string): Promise<{ agent: OpsAgent; runs: OpsAgentRun[] }> {
  return get<{ agent: OpsAgent; runs: OpsAgentRun[] }>(
    `/v1/ops/control/agents/${encodeURIComponent(id)}`
  );
}

/** Rewrite what an agent is told to do. The single most consequential edit here. */
export function updateAgentInstructions(id: string, instructions: string): Promise<{ agent: OpsAgent }> {
  return put<{ agent: OpsAgent }>(
    `/v1/ops/control/agents/${encodeURIComponent(id)}/instructions`, { instructions }
  );
}

export function patchOpsAgent(
  id: string, changes: { model?: string; effort?: string; cadence?: string }
): Promise<{ agent: OpsAgent }> {
  return patch<{ agent: OpsAgent }>(`/v1/ops/control/agents/${encodeURIComponent(id)}`, changes);
}

export function setAgentEnabled(id: string, enabled: boolean): Promise<{ agent: OpsAgent }> {
  return post<{ agent: OpsAgent }>(
    `/v1/ops/control/agents/${encodeURIComponent(id)}/enabled`, { enabled }
  );
}

export function listAgentRuns(agentId?: string, limit?: number): Promise<{ items: OpsAgentRun[] }> {
  const params = new URLSearchParams();
  if (agentId) params.set("agent", agentId);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return get<{ items: OpsAgentRun[] }>(`/v1/ops/control/runs${qs ? `?${qs}` : ""}`);
}

/**
 * The conversation with the chief of staff. Deliberately a queue rather than a
 * live socket: the agent is not always awake, it reads unanswered messages at
 * the start of its next run. The UI says so rather than spinning a fake typing
 * indicator.
 */
export function getOpsChat(): Promise<{ items: OpsChatMessage[]; unanswered: number }> {
  return get<{ items: OpsChatMessage[]; unanswered: number }>("/v1/ops/control/chat");
}

export function sendOpsChat(text: string): Promise<{ message: OpsChatMessage }> {
  return post<{ message: OpsChatMessage }>("/v1/ops/control/chat", { text });
}

export function getOpsFocus(): Promise<{ focus: OpsFocus }> {
  return get<{ focus: OpsFocus }>("/v1/ops/control/focus");
}

export function setOpsFocus(
  body: { headline: string; body: string; needsJared?: { ref?: string; title: string; why?: string }[] }
): Promise<{ focus: OpsFocus }> {
  return put<{ focus: OpsFocus }>("/v1/ops/control/focus", body);
}


/* -------------------------------------------------------------------------- */
/* The pacing governor behind /v1/ops/quota (migration 030).                   */
/*                                                                            */
/* The weekly quota does not roll over, so this is not a spend limiter: it is  */
/* a corridor with a floor as well as a ceiling, and the floor is the half     */
/* that stops the fleet doing an hour of work on Wednesday and nothing until   */
/* the following Tuesday.                                                     */
/* -------------------------------------------------------------------------- */

export function getQuota(): Promise<QuotaSnapshot> {
  return get<QuotaSnapshot>("/v1/ops/quota");
}

export function getBurndown(): Promise<{ items: BurndownPoint[] }> {
  return get<{ items: BurndownPoint[] }>("/v1/ops/quota/burndown");
}

export function getQuotaEvents(limit = 80, kind?: string): Promise<{ items: QuotaEvent[] }> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (kind) qs.set("kind", kind);
  return get<{ items: QuotaEvent[] }>(`/v1/ops/quota/events?${qs.toString()}`);
}

/** The block injected at the top of every agent prompt. Plain text on purpose:
 *  it is pasted verbatim, so it must be readable exactly as it is stored. */
export function getQuotaHeader(): Promise<string> {
  return get<string>("/v1/ops/quota/header");
}

export function patchQuotaConfig(
  changes: Partial<QuotaConfig>
): Promise<{ config: QuotaConfig; changed: string[] }> {
  return patch<{ config: QuotaConfig; changed: string[] }>("/v1/ops/quota/config", changes);
}

export function getSurplus(all = false): Promise<{ items: SurplusItem[] }> {
  return get<{ items: SurplusItem[] }>(`/v1/ops/quota/surplus${all ? "?all=1" : ""}`);
}

export function setSurplusEnabled(id: string, enabled: boolean): Promise<{ items: SurplusItem[] }> {
  return post<{ items: SurplusItem[] }>(
    `/v1/ops/quota/surplus/${encodeURIComponent(id)}/enabled`, { enabled }
  );
}


/* -------------------------------------------------------------------------- */
/* Demo mode — DEV ONLY. Every call here 404s when ORBIT_ENV=prod, which is how */
/* the sidebar and the page decide the feature does not exist in this           */
/* environment. Callers must treat a 404 as "unavailable", not as a fault.      */
/* -------------------------------------------------------------------------- */

export interface DemoModeState {
  enabled: boolean;
  tenantId: string | null;
  dataEpoch: string | null;
  refreshToken: number;
  appliedToken: number;
  runnerState: "stopped" | "starting" | "running" | "error";
  runnerMessage: string | null;
  runnerSeenAt: string | null;
  lastShiftAt: string | null;
  lastShiftDays: number | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface DemoModeResponse {
  available: true;
  environment: string;
  tenantId: string;
  tenantName: string | null;
  state: DemoModeState;
  runnerAlive: boolean;
  reps: { name: string; ageSeconds: number; moving: boolean }[];
  data: {
    newestInvoiceDate: string | null;
    newestWonDealDate: string | null;
    openDeals: number;
  };
}

export function fetchDemoMode(): Promise<DemoModeResponse> {
  return get<DemoModeResponse>("/v1/demo-mode");
}

export function setDemoModeEnabled(enabled: boolean): Promise<{ state: DemoModeState }> {
  return post<{ state: DemoModeState }>("/v1/demo-mode", { enabled });
}

/** Re-date the demo world to today without switching off and on again. */
export function refreshDemoModeData(): Promise<{ state: DemoModeState }> {
  return post<{ state: DemoModeState }>("/v1/demo-mode/refresh", {});
}
